import { useEffect } from 'react'
import { useLocation, useNavigate } from '@/router/navigation'
import { Button } from '@/shared/components'
import { clearEnrollmentStore, useEnrollmentStore } from '@/stores'
import { clearEnrollmentBrowserState } from '../state/enrollmentBrowserStorage'
import { ENROLLMENT_PUBLIC_LINKS } from '../constants/enrollmentContent'
import { getAgeFromBirthDate } from './applicationForm/utils'
import { getEnrollmentString } from '../utils/enrollmentHydration/helpers'

function clearLocalStorageKeepEmail() {
  clearEnrollmentStore()
  clearEnrollmentBrowserState()
}

export function ThankYouPage() {
  const navigate = useNavigate()
  const location = useLocation()
  const profile = useEnrollmentStore(state => state.userProfile)
  const resumedEnrollment = useEnrollmentStore(state => state.resumedEnrollment)
  const persistedBirthDate =
    profile?.personalData?.dateOfBirth || (resumedEnrollment ? getEnrollmentString(resumedEnrollment, ['dateOfBirth']) : '')
  const isPersistedUnder18 = (() => {
    const age = getAgeFromBirthDate(persistedBirthDate)
    return age !== null && age < 18
  })()
  const profileRecord = profile as Record<string, unknown> | null
  const resumedEnrollmentRecord = resumedEnrollment as Record<string, unknown> | null
  const isPersistedNoLicense =
    typeof profileRecord?.expectedLicenseMonth === 'number' &&
      typeof profileRecord?.expectedLicenseYear === 'number' ||
    typeof resumedEnrollmentRecord?.expectedLicenseMonth === 'number' &&
      typeof resumedEnrollmentRecord?.expectedLicenseYear === 'number'
  // If redirected from under 18, pass { state: { under18: true } }
  const isUnder18 = Boolean(location.state && location.state.under18) || isPersistedUnder18
  const isNoLicense =
    !isUnder18 && (Boolean(location.state && location.state.noLicense) || isPersistedNoLicense)
  const completedAdditionalInfo = location.state && location.state.completedAdditionalInfo
  const hasLicenseStateMismatch = location.state && location.state.licenseStateMismatch
  const appointmentScheduled = location.state && location.state.appointmentScheduled
  const callRequestSubmitted = location.state && location.state.callRequest

  useEffect(() => {
    if (appointmentScheduled || callRequestSubmitted) return
    clearLocalStorageKeepEmail()
  }, [appointmentScheduled, callRequestSubmitted])

  return (
    <div className="bg-white px-4 py-20">
      <div className="mx-auto max-w-4xl">
        <div className="mb-5 flex items-start justify-center animate-fade-in-up">
          <h1 className="modal-heading__title text-center">Thank You!</h1>
        </div>

        <div className="mx-auto max-w-3xl pt-6 flex flex-col items-center text-center">
          <p
            className="animate-fade-in-up"
            style={{ animationDelay: '80ms', animationFillMode: 'both' }}
          >
            Thank you for submitting your information.
            {isUnder18 && (
              <>
                <br />
                We'll contact you as you become eligible with scheduling options.
              </>
            )}
            {isNoLicense && (
              <>
                <br />
                We'll send reminders and next steps when you're licensed and ready to continue.
              </>
            )}
            {!isUnder18 && !appointmentScheduled && !callRequestSubmitted && (
              <>
                <br />
                One of our representatives will contact you soon.
              </>
            )}
            {callRequestSubmitted && (
              <>
                <br />
                One of our representatives will call you soon.
              </>
            )}
            {appointmentScheduled && (
              <>
                <br />
                We look forward to seeing you at your scheduled information session.
              </>
            )}
          </p>

          <div
            className="my-7 flex items-center justify-center md:my-9"
            style={{ animationFillMode: 'both' }}
          >
            <svg
              viewBox="0 0 80 80"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
              className="h-20 w-20 md:h-24 md:w-24"
              aria-label="Success Checkmark"
            >
              <circle cx="40" cy="40" r="36" fill="#E65300" />
              <path
                d="M24 42l14 14 18-26"
                fill="none"
                stroke="#fff"
                strokeWidth="6"
                strokeLinecap="round"
                strokeLinejoin="round"
                style={{
                  strokeDasharray: 70,
                  strokeDashoffset: 70,
                  animation: 'checkmark-draw 1s 0.2s forwards cubic-bezier(0.23, 1, 0.32, 1)'
                }}
              />
              <style>{`
                @keyframes checkmark-draw {
                  to {
                    stroke-dashoffset: 0;
                  }
                }
              `}</style>
            </svg>
          </div>

          {!isUnder18 && !isNoLicense && !completedAdditionalInfo && !hasLicenseStateMismatch && (
            <>
              <p
                className="mb-7 max-w-3xl text-base font-medium leading-snug text-primary md:mb-9 animate-fade-in-up"
                style={{ animationDelay: '240ms', animationFillMode: 'both' }}
              >
                {appointmentScheduled
                  ? 'While you wait for your information session, please provide some additional information to help streamline the conversation.'
                  : 'While you wait for the Enrollment Advisor to call you, please provide some additional information to help streamline the conversation.'}
              </p>
              <div
                className="flex w-full max-w-[330px] flex-col gap-3.5 animate-fade-in-up"
                style={{ animationDelay: '320ms', animationFillMode: 'both' }}
              >
                <Button
                  onClick={() =>
                    navigate('/self-enroll', { state: { startStep: 1, limitToStep: 3 } })
                  }
                  variant="primary"
                  fullWidth
                  className="hover:-translate-y-0.5 active:scale-95"
                >
                  Add More Information
                </Button>
              </div>
            </>
          )}
          <Button
            onClick={() =>
              window.open(ENROLLMENT_PUBLIC_LINKS.cdlTraining, '_blank', 'noopener,noreferrer')
            }
            variant="secondary"
            className="hover:-translate-y-0.5 active:scale-95 mt-4 max-w-xs mx-auto"
          >
            Learn More about CDL Training
          </Button>
        </div>
      </div>
    </div>
  )
}
